/**
 * BriefDeckPreview — react-pdf viewer for the generated brief deck.
 *
 * TASK-406: pruned from DocumentViewerPanel.tsx. Renders the PDF rendition
 * of the LLM-generated PPTX produced by the document-export service.
 *
 * Props:
 *   pdfUrl: presigned URL pointing at the PDF in MinIO (1h TTL)
 *   isRegenerating: shows a translucent overlay so the user sees the deck
 *                   blur-update rather than blank-then-load when the chat
 *                   triggers a regenerate.
 *   onPageChange: optional — emit page navigation events to the parent.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Loader2,
  AlertCircle,
} from 'lucide-react'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker

interface BriefDeckPreviewProps {
  pdfUrl: string | null
  isRegenerating?: boolean
  emptyState?: React.ReactNode
}

const BriefDeckPreview: React.FC<BriefDeckPreviewProps> = ({
  pdfUrl,
  isRegenerating = false,
  emptyState,
}) => {
  const [numPages, setNumPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [scale, setScale] = useState(0.9)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: pages }: { numPages: number }) => {
      setNumPages(pages)
      setCurrentPage((p) => Math.min(p, pages))
      setPdfError(null)
    },
    [],
  )

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('[BriefDeckPreview] PDF load error:', error)
    setPdfError('Failed to render deck preview. Try regenerating.')
  }, [])

  // TASK-407: reset error state when the source URL changes so a successful
  // regenerate can clear a stale error from a prior failed export.
  useEffect(() => {
    setPdfError(null)
    setNumPages(0)
    setCurrentPage(1)
  }, [pdfUrl])

  const goToPrevPage = () => setCurrentPage((p) => Math.max(1, p - 1))
  const goToNextPage = () => setCurrentPage((p) => Math.min(numPages || 1, p + 1))
  const zoomIn = () => setScale((s) => Math.min(2.0, s + 0.15))
  const zoomOut = () => setScale((s) => Math.max(0.4, s - 0.15))

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        {emptyState ?? (
          <div className="text-center text-gray-500">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No deck generated yet.</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative flex flex-col h-full bg-gray-50">
      {/* Page nav + zoom toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200 text-sm text-gray-600">
        <button
          onClick={goToPrevPage}
          disabled={currentPage <= 1}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="tabular-nums">
          {currentPage} / {numPages || '?'}
        </span>
        <button
          onClick={goToNextPage}
          disabled={currentPage >= numPages}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        <button onClick={zoomOut} className="p-1 rounded hover:bg-gray-100" aria-label="Zoom out">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn} className="p-1 rounded hover:bg-gray-100" aria-label="Zoom in">
          <ZoomIn className="w-4 h-4" />
        </button>
      </div>

      {/* Deck pane */}
      <div className="flex-1 overflow-auto flex justify-center py-4">
        {pdfError ? (
          <div className="text-center text-red-500 pt-12">
            <AlertCircle className="w-10 h-10 mx-auto mb-3" />
            <p className="text-sm">{pdfError}</p>
          </div>
        ) : (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="text-center pt-12 text-gray-500">
                <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
                <p className="text-sm">Loading deck…</p>
              </div>
            }
          >
            <Page
              pageNumber={currentPage}
              scale={scale}
              renderTextLayer
              renderAnnotationLayer
              className="shadow-lg"
            />
          </Document>
        )}
      </div>

      {/* Regenerating overlay */}
      {isRegenerating && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="bg-white/90 border border-gray-200 rounded-lg px-4 py-3 shadow-md flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <span className="text-sm text-gray-700">Updating preview…</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default BriefDeckPreview
